import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { NavLink } from "react-router-dom";
import Button from "./Button";
import aboutMeImg from "../images/av2.jpeg";

/**
 * Represents the About Me section.
 * Displays information about the user.
 *
 * @component
 * @param {string} name - The name of the user.
 */

const AboutMe = ({ name }) => {
  // Using react-intersection-observer to determine if the component is in view
  const [ref, inView] = useInView({
    threshold: 0.4,
    triggerOnce: true,
  });

  // Variants for staggered animations
  const staggerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  // Variants for paragraph animations
  const paragraphVariants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
  };

  return (
    <section className="about">
      <div className="aboutContainer container">
        <div className="row">
          <motion.div
            className="personalImage col-12 col-lg-6"
            ref={ref}
            initial={{ x: "-10vw", opacity: 0, scale: 0.5 }}
            animate={inView ? { x: 0, opacity: 1, scale: 1 } : { x: "-10vw", opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            whileHover={{ scale: 1.05 }}
          >
            {/* Display the personal image */}
            <motion.img src={aboutMeImg} alt={name} />
          </motion.div>
          <div className="personalInfo col-12 col-lg-6">
            <motion.div className="contentContainer" variants={staggerVariants}>
              {/* Display greeting and job title with animation */}
              <motion.h4 variants={paragraphVariants}>Nice to meet you! 👋🏻</motion.h4>
              <motion.h5 variants={paragraphVariants}>I'm a Digital Transformation Engineer.</motion.h5>

              {/* Display content description with animation */}
              <motion.div
                className="contentDescription"
                variants={staggerVariants}
                initial="initial"
                animate={inView ? "animate" : "initial"}
              >
                {/* Paragraphs with animation */}
                <motion.p variants={paragraphVariants}>
                 I am a Digital Transformation Engineer with a specialization in <span style={{ color: "var(--hl-color)" }}>IoT-based architectures </span> 
                and <span style={{ color: "var(--hl-color)" }}> embedded systems</span>. Throughout my extensive career, 
                I have successfully collaborated with a wide range of enterprises to develop cutting-edge, <span style={{ color: "var(--hl-color)" }}>interconnected solutions</span> .
                 
                </motion.p>
                <br />
                <motion.p variants={paragraphVariants}>
                My proven ability to design tailored <span style={{ color: "var(--hl-color)" }}>IoT solutions</span>, leveraging data to optimize efficiency and operational performance, sets me apart.
                Additionally, my expertise in <span style={{ color: "var(--hl-color)" }}>embedded systems </span>has been sharpened through hands-on involvement in projects from their conception to their final implementation. 
                </motion.p>
                <br />
                <motion.p variants={paragraphVariants}>
                I am committed to bridging the gap between the physical and digital realms, enabling businesses to flourish in the era of <span style={{ color: "var(--hl-color)" }}>digital transformation</span>.
                </motion.p>
              </motion.div>

              {/* Button to view the portfolio */}
              <NavLink to="/portfolio">
                <Button name="View Portfolio" />
              </NavLink>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMe;
